import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { useSearch } from '../api/admin'
import { fileUrl } from '../api/hooks'
import { Badge, Empty, GradeBadge, StatusBadge, fmtDate, fmtDateTime, severityColor } from '../components/ui'

export default function SearchPage() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const q = params.get('q') ?? ''
  const [text, setText] = useState(q)
  useEffect(() => setText(q), [q])
  useEffect(() => {
    const h = setTimeout(() => { if (text !== q) setParams(text ? { q: text } : {}) }, 350)
    return () => clearTimeout(h)
  }, [text, q, setParams])
  const r = useSearch(q)
  const d = r.data
  const total = d ? d.reports.length + d.findings.length + d.actions.length + d.files.length : 0

  return (
    <div className="stack">
      <h2>{t('search.title')}</h2>
      <div className="card">
        <input autoFocus placeholder={t('search.placeholder')} value={text} onChange={(e) => setText(e.target.value)} style={{ fontSize: '1.05rem' }} />
        <p className="muted small" style={{ marginBottom: 0 }}>{t('search.hint')}</p>
      </div>
      {q.trim().length < 2 ? null : r.isLoading ? <p>{t('common.loading')}</p> : !d || total === 0 ? <Empty text={t('search.empty', { q })} /> : (
        <>
          {d.reports.length ? (
            <div className="card">
              <h3>{t('nav.reports')} <span className="muted small">({d.reports.length})</span></h3>
              <div className="table-wrap"><table className="table compact">
                <thead><tr><th>{t('reports.code')}</th><th>{t('reports.title')}</th><th>{t('companies.name')}</th><th>{t('reports.date')}</th><th>{t('reports.status')}</th><th>{t('reports.result')}</th><th className="num">{t('reports.findings')}</th></tr></thead>
                <tbody>{d.reports.map((x) => (
                  <tr key={x.id}><td><Link to={`/reports/${x.id}`}><b>{x.code ?? x.templateCode}</b></Link></td><td>{x.title}</td><td className="small">{x.companyName}</td><td>{fmtDate(x.auditStartDate)}</td><td><StatusBadge status={x.status} /></td><td><GradeBadge grade={x.overallGrade} percent={x.overallPercent} /></td><td className="num">{x.findingCount}</td></tr>
                ))}</tbody>
              </table></div>
            </div>
          ) : null}
          {d.findings.length ? (
            <div className="card">
              <h3>{t('form.findings')} <span className="muted small">({d.findings.length})</span></h3>
              <div className="table-wrap"><table className="table compact">
                <thead><tr><th>#</th><th>{t('finding.severity')}</th><th>{t('finding.description')}</th><th>{t('finding.action')}</th><th>{t('reports.code')}</th><th>{t('actions.status')}</th></tr></thead>
                <tbody>{d.findings.map((f) => (
                  <tr key={f.id}><td>{f.number ?? '–'}{f.questionNo ? <div className="muted small">S.{f.questionNo}</div> : null}</td><td><Badge color={severityColor[f.severity]}>{t(`severity.${f.severity}`)}</Badge></td><td className="small">{f.description}</td><td className="small">{f.actionText}</td><td><Link to={`/reports/${f.formInstanceId}`}>{f.reportCode ?? t('reports.open')}</Link><div className="muted small">{f.companyName}</div></td><td className="small">{f.actionStatus ? t(`actionStatus.${f.actionStatus}`) : '–'}</td></tr>
                ))}</tbody>
              </table></div>
            </div>
          ) : null}
          {d.actions.length ? (
            <div className="card">
              <h3>{t('nav.actions')} <span className="muted small">({d.actions.length})</span></h3>
              <div className="table-wrap"><table className="table compact">
                <thead><tr><th>{t('finding.actionNo')}</th><th>{t('actions.status')}</th><th>{t('finding.action')}</th><th>{t('finding.responsible')}</th><th>{t('finding.dueDate')}</th><th>{t('companies.name')}</th></tr></thead>
                <tbody>{d.actions.map((a) => (
                  <tr key={a.id}><td><Link to={`/actions?id=${a.id}`}><b>{a.actionNo}</b></Link></td><td><Badge>{t(`actionStatus.${a.status}`)}</Badge></td><td className="small">{a.actionText}</td><td className="small">{a.responsibleName}</td><td>{fmtDate(a.dueDate)}</td><td className="small">{a.companyName}</td></tr>
                ))}</tbody>
              </table></div>
            </div>
          ) : null}
          {d.files.length ? (
            <div className="card">
              <h3>{t('archive.files')} <span className="muted small">({d.files.length})</span></h3>
              <div className="table-wrap"><table className="table compact">
                <thead><tr><th>{t('archive.fileName')}</th><th>{t('archive.kind')}</th><th>{t('reports.code')}</th><th>{t('reports.date')}</th><th /></tr></thead>
                <tbody>{d.files.map((f) => (
                  <tr key={f.attachmentId}><td>{f.fileName}<div className="muted small">{(f.sizeBytes / 1024).toFixed(0)} KB</div></td><td><Badge>{t(`attachment.${f.kind}`, f.kind)}</Badge></td><td><Link to={`/reports/${f.formInstanceId}`}>{f.reportCode ?? t('reports.open')}</Link></td><td>{fmtDateTime(f.createdAt)}</td><td><button className="ghost" onClick={() => void fileUrl(f.fileId).then((u) => window.open(u, '_blank'))}>{t('archive.download')}</button></td></tr>
                ))}</tbody>
              </table></div>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
